import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  ReactNode
} from 'react';
import axios from 'axios';
import { useLogin, useRefresh } from '../api-calls/auth.queries';
import { useNavigate } from 'react-router-dom';
import { routes } from '../constants';

interface User {
  id: string;
  email: string;
  role: string;
  accessToken: string;
  salesforceContactId: string;
}

interface AuthContextType {
  user: User | null;
  login: (data: { email: string; password: string }) => Promise<void>;
  logout: () => void;
  error: string | null;
  token: string | null;
  loading: boolean;
  refreshing: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { mutateAsync: loginMutate } = useLogin();
  const { mutateAsync: refreshMutate } = useRefresh();

  useEffect(() => {
    const storedToken = localStorage.getItem('accessToken');
    const storedUser = localStorage.getItem('user');
    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (error) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        setUser(null);
        setToken(null);
      }
    }
    setLoading(false);
  }, []);

  const handleUserLogin = (userData: User, accessToken: string) => {
    setUser(userData);
    setToken(accessToken);
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const login = async (data: { email: string; password: string }) => {
    try {
      setError(null);
      const response = await loginMutate(data);
      const { user: userData, accessToken } = response?.data;
      handleUserLogin(userData, accessToken);
      return 'success';
    } catch (error) {
      let errorMessage =
        'An unexpected error occurred. Please try again in a few minutes or contact support.';
      const isRateLimitError =
        error?.response?.data ===
        'Too many login attempts, please try again later.';
      const isInvalidEmailOrPasswordError =
        error?.response?.data?.message === 'Invalid email or password';
      if (isRateLimitError) {
        errorMessage = 'Too many login attempts, please try again later.';
      } else if (isInvalidEmailOrPasswordError) {
        errorMessage = 'Invalid email or password.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('accessToken');
    navigate(routes.LOGIN);
  };

  useLayoutEffect(() => {
    const authInterceptor = axios.interceptors.request.use((config) => {
      config.headers.Authorization =
        !config._retry && token
          ? `Bearer ${token}`
          : config.headers.Authorization;

      return config;
    });

    return () => {
      axios.interceptors.request.eject(authInterceptor);
    };
  }, [token]);

  useLayoutEffect(() => {
    const refreshInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (!originalRequest._retryCount) {
          originalRequest._retryCount = 0;
        }

        if (
          error.response?.status === 403 &&
          ['Invalid token', 'Unauthorized'].includes(
            error.response.data.message
          ) &&
          !refreshing &&
          originalRequest._retryCount < 3
        ) {
          setRefreshing(true);
          originalRequest._retryCount += 1;
          try {
            const response = await refreshMutate({});
            const { accessToken, user: userData } = response?.data;
            handleUserLogin(userData, accessToken);
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            originalRequest._retry = true;
            setRefreshing(false);
            return axios(originalRequest);
          } catch (err) {
            logout();
            return Promise.reject(err);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(refreshInterceptor);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        error,
        loading,
        refreshing,
        login,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
